import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import {
  SearchResultEntityType,
  SearchResultLayerCheckService,
} from '@lassi/webapp-shared';
import { of, switchMap } from 'rxjs';
import { LoadingIndicatorComponent } from './loading-indicator.component';
import { NoticeComponent } from './notice.component';

@Component({
  selector: 'lib-search-result-layer-check',
  imports: [NoticeComponent, MatButton, LoadingIndicatorComponent],
  template: `
    @if (isQueryLoading) {
      <lib-loading-indicator />
    } @else if (missingLayers().length > 0) {
      <lib-notice noticeType="warning">
        <p class="text mat-label-medium">
          If you cannot see the results on the map, the relevant layers may not
          be turned on
        </p>
        <button
          mat-button
          class="btn"
          color="primary"
          (click)="onEnableLayers()"
        >
          Click to turn on
        </button>
      </lib-notice>
    }
  `,
  styles: `
    .text {
      margin-top: 0;
      margin-bottom: 0;
    }
    .btn {
      padding-left: 0;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultLayerCheckComponent {
  private readonly layerCheckService = inject(SearchResultLayerCheckService);

  searchType = input<SearchResultEntityType | null>(null);

  /**
   * Entity identifiers:
   * - surveyMark → pfi values from SurveyMarkSearchDto (string of numeric mark_id)
   * - surveyLabel → pfi values from SurveyLabelDto
   * - address / lot / parcel / property / crownDescription / coordinates
   *   → not used (static mapping or no layer check)
   */
  identifiers = input<string[]>([]);

  private readonly requiredLayersResource = rxResource({
    params: () => {
      const type = this.searchType();
      return type ? { type, ids: this.identifiers() } : undefined;
    },
    stream: ({ params }) =>
      params
        ? this.layerCheckService.getRequiredLayerNames(params.type, params.ids)
        : of([]),
  });

  isQueryLoading = this.requiredLayersResource.isLoading();

  missingLayers = toSignal(
    toObservable(
      computed(() =>
        this.requiredLayersResource.hasValue()
          ? this.requiredLayersResource.value()
          : [],
      ),
    ).pipe(
      switchMap((required) =>
        this.layerCheckService.getMissingLayers(required),
      ),
    ),
    { initialValue: [] },
  );

  async onEnableLayers() {
    await this.layerCheckService.enableLayers(this.missingLayers());
  }
}
